import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import { DeepLinkType } from '@gem/models/deeplinksModel';
import { platformInfo } from '@gem/utils';

import { storage } from 'APP/packages/storage';
import { RouterLink, RouterParams } from 'APP/router/constants';
import { buildPath } from 'APP/router/helpers/buildPath';
import useGetParameter from 'APP/router/hooks/useGetParameter';
import { UnauthorizedRouts } from 'APP/unauthorized/UnauthorizedView.constants';
import Entities from 'STORE';

import { IUnauthorizedDeepLinkHandlerStore } from './UnauthorizedDeepLinkHandler.types';

export const useUnauthorizedDeepLinkHandlerPresenter = async (): Promise<void> => {
  const navigate = useNavigate();
  const isDeeplinkLanding = useMatch(RouterLink.Deeplink);

  const isDeepLink = useGetParameter(RouterParams.deepLink);
  const deepLinkType = useGetParameter(RouterParams.type) as DeepLinkType;
  const channelId = useGetParameter(RouterParams.channelId);
  const allowGuests = useGetParameter(RouterParams.allowGuests);
  const isAllowGuests = allowGuests === 'true';
  const invitationUrl = useGetParameter(RouterParams.invitationUrl);

  const presenter = useLocalStore<IUnauthorizedDeepLinkHandlerStore>(() => ({
    isNeedHandleDeepLink: true,

    get isAuthFinished(): boolean {
      return Entities.authStore.isFirstAuthDone;
    },

    get isAuthenticated(): boolean {
      return Entities.authStore.isAuthenticated;
    },

    finishDeepLinkHandling(): void {
      presenter.isNeedHandleDeepLink = false;
    },

    handleDeepLink(): void {
      if (isDeepLink && deepLinkType === DeepLinkType.Room && channelId) {
        storage.guestCall.setGuestCallChannelId(channelId);
        storage.guestCall.setGuestCallInvitationUrl(invitationUrl);
        storage.guestCall.setGuestCallAllowGuests(isAllowGuests);

        Entities.callInvitation.setChannelId(channelId || null);
        setTimeout(() => {
          if (platformInfo.isMobile && !isAllowGuests) {
            navigate(
              buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitationAccessDisabled]),
              {
                replace: true,
              }
            );
          } else {
            navigate(
              buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
                channelId,
              }),
              {
                replace: true,
              }
            );
          }
        }, 1);
      }
      presenter.finishDeepLinkHandling();
    },
  }));

  useEffect(() => {
    if (isDeeplinkLanding) {
      presenter.isNeedHandleDeepLink = false;
    }
  }, [isDeeplinkLanding]);

  useEffect(() => {
    if (presenter.isAuthFinished && !presenter.isAuthenticated && presenter.isNeedHandleDeepLink) {
      presenter.handleDeepLink();
    }
  }, [presenter.isAuthFinished, presenter.isNeedHandleDeepLink]);
};
